import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { authedFetch } from '@/lib/authedFetch'

const API_BASE = import.meta.env.VITE_NEST_API_URL || ''

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  // authedFetch attaches the idToken, sets credentials, and retries once with a
  // refreshed token on 401 so a stale token heals instead of 401ing the app.
  const res = await authedFetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || res.statusText)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

interface NestContextValue {
  lists: { owned: any[]; shared: any[] } | null
  users: any[]
  notificationCount: { unseen_lists: number; unseen_items: number }
  listItems: Record<number, any[]>
  refreshLists: () => Promise<void>
  refreshUsers: () => Promise<void>
  fetchListItems: (listId: number) => Promise<any[]>
  addItemToList: (listId: number, sourceType: string, sourceId: string) => Promise<void>
  removeItemFromList: (listId: number, itemId: number) => Promise<void>
  renameList: (listId: number, name: string) => Promise<void>
  deleteList: (listId: number) => Promise<void>
  shareList: (listId: number, userId: number, permission?: 'read' | 'write') => Promise<void>
  unshareList: (listId: number, userId: number) => Promise<void>
}

const NestContext = createContext<NestContextValue | null>(null)

export function NestProvider({ children }: { children: ReactNode }) {
  const [lists, setLists] = useState<{ owned: any[]; shared: any[] } | null>(null)
  const [users, setUsers] = useState<any[]>([])
  const [notificationCount, setNotificationCount] = useState({ unseen_lists: 0, unseen_items: 0 })
  const [listItems, setListItems] = useState<Record<number, any[]>>({})

  const refreshLists = useCallback(async () => {
    try {
      const result = await apiFetch<{ owned: any[]; shared: any[] }>('/api/lists')
      setLists(result)
    } catch {
      // nest-api Worker only exists on nest.thephenom.app — silently degrade elsewhere
    }
  }, [])

  const refreshUsers = useCallback(async () => {
    try {
      const result = await apiFetch<{ users: any[] }>('/api/users')
      const shaped = result.users.map((u: any) => {
        const ghIdMatch = u.avatar_url?.match(/\/u\/(\d+)/)
        return {
          id: u.id,
          github_id: ghIdMatch ? parseInt(ghIdMatch[1]) : null,
          login: u.github_login || u.email?.split('@')[0],
          email: u.email,
          // Raw profile name fields — chat sender resolution prefers these over
          // the email-fallback display_name below (see sender-name.ts).
          name: u.name ?? null,
          username: u.username ?? null,
          display_name: u.display_name || u.email,
          avatar_url: u.avatar_url,
          profile_url: null,
          has_logged_in: true,
        }
      })
      setUsers(shaped)
    } catch (e) {
      console.error('[NestProvider] users fetch failed:', e)
    }
  }, [])

  const refreshNotifications = useCallback(async () => {
    try {
      const result = await apiFetch<{ unseen_lists: number; unseen_items: number }>('/api/notifications/count')
      setNotificationCount(result)
    } catch {
      // silent
    }
  }, [])

  const fetchListItems = useCallback(async (listId: number) => {
    try {
      const result = await apiFetch<{ items: any[] }>(`/api/lists/${listId}/items`)
      setListItems(prev => ({ ...prev, [listId]: result.items }))
      return result.items
    } catch (e) {
      console.error('[NestProvider] fetchListItems failed:', e)
      return []
    }
  }, [])

  const addItemToList = useCallback(async (listId: number, sourceType: string, sourceId: string) => {
    await apiFetch(`/api/lists/${listId}/items`, {
      method: 'POST',
      body: JSON.stringify({ source_type: sourceType, source_id: sourceId }),
    })
    // Refresh both list counts and items for this list
    await Promise.all([refreshLists(), fetchListItems(listId)])
  }, [refreshLists, fetchListItems])

  const removeItemFromList = useCallback(async (listId: number, itemId: number) => {
    await apiFetch(`/api/lists/${listId}/items/${itemId}`, { method: 'DELETE' })
    await Promise.all([refreshLists(), fetchListItems(listId)])
  }, [refreshLists, fetchListItems])

  const renameList = useCallback(async (listId: number, name: string) => {
    await apiFetch(`/api/lists/${listId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    })
    await refreshLists()
  }, [refreshLists])

  const deleteList = useCallback(async (listId: number) => {
    await apiFetch(`/api/lists/${listId}`, { method: 'DELETE' })
    setListItems(prev => {
      const next = { ...prev }
      delete next[listId]
      return next
    })
    await refreshLists()
  }, [refreshLists])

  const shareList = useCallback(async (listId: number, userId: number, permission: 'read' | 'write' = 'write') => {
    await apiFetch(`/api/lists/${listId}/share`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, permission }),
    })
    // Refresh so shared_with_ids (the checkmarks) reflect the change.
    await refreshLists()
  }, [refreshLists])

  const unshareList = useCallback(async (listId: number, userId: number) => {
    await apiFetch(`/api/lists/${listId}/share/${userId}`, { method: 'DELETE' })
    await refreshLists()
  }, [refreshLists])

  // Keyed on idToken: the initial bootstrap fired once on mount, so a render
  // before auth was ready (or after the token expired) 401'd and left lists /
  // users empty forever ("No lists yet" with 3 lists actually present). Re-run
  // when the token becomes available or is refreshed.
  const idToken = useAuthStore((s) => s.idToken)
  useEffect(() => {
    if (!idToken) return
    refreshLists()
    refreshUsers()
    refreshNotifications()
    const interval = setInterval(refreshNotifications, 30_000)
    return () => clearInterval(interval)
  }, [idToken, refreshLists, refreshUsers, refreshNotifications])

  return (
    <NestContext.Provider value={{
      lists, users, notificationCount, listItems,
      refreshLists, refreshUsers, fetchListItems,
      addItemToList, removeItemFromList,
      renameList, deleteList, shareList, unshareList,
    }}>
      {children}
    </NestContext.Provider>
  )
}

export function useNest() {
  const ctx = useContext(NestContext)
  if (!ctx) throw new Error('useNest must be used within <NestProvider>')
  return ctx
}
